import {Box} from "@mui/material";
import {FC, useState, useCallback} from "react";
import {makeStyles} from "tss-react/mui";
import {NoteDto, UpdateNoteBodyDto} from "@/app2/shared/data/gen";
import {useUpdateNote, useDeleteNote} from "@/app2/shared/data";
import {NoteItemCard, NoteItemCardProps} from "./NoteItemCard";

const useStyles = makeStyles()((theme) => ({
	noteItem: {
		[theme.breakpoints.down("sm")]: {
			order: 1
		}
	},
	cardRoot: {
		backgroundColor: theme.palette.background.paper,
		border: `1px solid ${theme.palette.secondary.light}`
	},
}));

/** Mirrors shapeshift notesLibrary/noteItem - container: state, API, delegates to NoteItemCard */
export interface NoteItemProps {
	note: NoteDto;
	onGoToModule?: (courseSlug: string, moduleSlug: string, moduleType: string, timestamp?: number) => void;
	onRefresh: () => void;
	className?: string;
}

export const NoteItem: FC<NoteItemProps> = ({note, onGoToModule, onRefresh, className}) => {
	const {classes, cx} = useStyles();
	const [isEditing, setIsEditing] = useState(false);
	const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);

	const {mutate: updateNote, isPending: isUpdating} = useUpdateNote();
	const {mutate: deleteNote, isPending: isDeleting} = useDeleteNote();

	const {course, module} = note;
	const isSourceInvalid = !course?.slug || !module?.slug;

	/** Per user: lesson/course name not displayed in legacy - pass empty links */
	const links: JSX.Element[] = [];

	const handleUpdate = useCallback(
		(newValue: string) => {
			if (newValue.trim() === note.content) {
				setIsEditing(false);
				return;
			}
			const body: UpdateNoteBodyDto = {content: newValue.trim(), videoTimestamp: note.videoTimestamp};
			updateNote(
				{noteId: note.id, ...body},
				{
					onSuccess: () => {
						setIsEditing(false);
						onRefresh();
					}
				}
			);
		},
		[note.id, note.content, note.videoTimestamp, updateNote, onRefresh]
	);

	const handleDeleteNote = useCallback(() => {
		deleteNote(note.id, {
			onSuccess: () => {
				setConfirmDeleteModalOpen(false);
				onRefresh();
			}
		});
	}, [note.id, deleteNote, onRefresh]);

	/** Defer setState to avoid ResizeObserver loop when Masonry recalculates layout on edit toggle */
	const setIsEditingDeferred = useCallback((action: React.SetStateAction<boolean>) => {
		setTimeout(() => setIsEditing(action), 0);
	}, []);

	const noteItemCardProps: NoteItemCardProps = {
		note,
		isSourceInvalid,
		onGoToModuleAction: onGoToModule,
		isEditing,
		setIsEditing: setIsEditingDeferred,
		confirmDeleteModalOpen,
		setConfirmDeleteModalOpen,
		handleDeleteNote,
		handleUpdate,
		links,
		className: classes.cardRoot,
		isUpdating,
		isDeleting
	};

	return (
		<Box className={cx(classes.noteItem, className)}>
			<NoteItemCard {...noteItemCardProps} />
		</Box>
	);
};
